"use client";
import React, { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import { graphQlFetchFunction } from "@packages/lib/server-actions/server-action";
import { leadFormPageQuery } from "@packages/lib/graphQL/graphql-query";
import { getFormUrlSlug } from "@packages/shared-components/leadform/field-registry";
import {
  LeadFormCmsItem,
  LeadFormSubmissionContext,
} from "@packages/shared-components/leadform/types";
import { getProjectSpecificData } from "@packages/lib/utlils/commonFunction";
import { getCookie } from "@packages/lib/utlils/cookie-utils";
import API_URL from "@packages/lib/server-actions/api-urls";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import { v4 as uuidv4 } from "uuid";
import UniversityCard from "@packages/shared-components/leadform/university-card";
import LeadformSection from "@packages/shared-components/leadform/leadformsection";
import Logo from "@packages/shared-components/user-profile/signin-signup/logo";
import LeadFormFieldsSkeleton from "@packages/shared-components/skeleton/leadform-fields-skeleton";

/**
 * Request Info lead form page (client component).
 *
 * URL pattern built by the CTA:
 *   /degrees/email/{collegeName}-email/{collegeId}/{courseId}/{subject}/n-{subOrderItemId}/send-college-email.html
 */
export default function RequestInfoPage() {
  const params = useParams();
  const slug = params?.slug as string[] | undefined;

  const [cmsItems, setCmsItems] = useState<LeadFormCmsItem[]>([]);
  const [submissionContext, setSubmissionContext] = useState<LeadFormSubmissionContext | null>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const hasValidLength = slug.length === 4 || slug.length === 5 || slug.length === 6;
    if (!hasValidLength) {
      setIsNotFound(true);
      return;
    }

    const hasCollegeNameSegment = slug[0]?.endsWith("-email");
    const baseIndex = hasCollegeNameSegment ? 1 : 0;

    const collegeId = slug[baseIndex];
    const courseId = slug[baseIndex + 1] || "0";
    const subject = slug[baseIndex + 2];
    const maybeSubOrderSegment = slug.length === baseIndex + 5 ? slug[baseIndex + 3] : null;
    const terminalSegment = slug.length === baseIndex + 5 ? slug[baseIndex + 4] : slug[baseIndex + 3];
    console.log(courseId,"courseIdcourseId")
    if (!subject || terminalSegment !== "send-college-email.html") {
      setIsNotFound(true);
      return;
    }
    if (maybeSubOrderSegment && !/^n-\d+$/.test(maybeSubOrderSegment)) {
      setIsNotFound(true);
      return;
    }
    if (!collegeId || isNaN(Number(collegeId)) || Number(collegeId) <= 0) {
      setIsNotFound(true);
      return;
    }
    if (!courseId || isNaN(Number(courseId)) || Number(courseId) < 0) {
      setIsNotFound(true);
      return;
    }

    const fetchData = async () => {
      try {
        // Build enquiry headers — include auth token if user is signed in (same as prospectus page)
        const idToken = getCookie("wuIdToken");
        const subOrderItemId = maybeSubOrderSegment ? maybeSubOrderSegment.replace("n-", "") : "";
        const enquiryHeaders: Record<string, string> = {
          "x-api-key": `${process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY}`,
        };
        const getEnquiryPayload: any = {
          suborderItemId: Number(subOrderItemId) || 0,
          orderItemId: "",
          collegeId: Number(collegeId),
          courseId: Number(courseId),
          affiliateId: getProjectSpecificData("affiliateId"),
          qualCode: "",
        };
        if (idToken) {
          enquiryHeaders["Authorization"] = `Bearer ${idToken}`;
          enquiryHeaders["Lp"] = "Y";
          getEnquiryPayload.purpose = "PROSPECTUS_TYPE";
          getEnquiryPayload.enquiryDetails = [
            {
              suborderItemId: Number(subOrderItemId) || 0,
              institutionId: Number(collegeId),
            },
          ];
        }

        // Fetch CMS, overview, and enquiry details in parallel
        const [cmsResponse, overviewResponse, enquiryData] = await Promise.all([
          graphQlFetchFunction(leadFormPageQuery(getFormUrlSlug("requestinfo"))),
          fetch(
            `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}${API_URL.leadProfileInstitutionOverview}?institutionId=${collegeId}&networkId=${getProjectSpecificData("networkId")}&affiliateId=${getProjectSpecificData("affiliateId")}${courseId && courseId !== "0" ? `&courseId=${courseId}` : ""}`,
            {
              method: "GET",
              headers: {
                "x-api-key": process.env.NEXT_PUBLIC_X_API_KEY!,
                sitecode: String(process.env.SITE_CODE || "WU"),
              },
            }
          ),
          makeApiCall({
            url: `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}/${API_URL.getEnquiryDetails}`,
            method: "POST",
            body: getEnquiryPayload,
            headers: enquiryHeaders,
          }),
        ]);

        // Process CMS
        const pageData = cmsResponse?.data?.pageTemplateDynamicPageCollection?.items?.[0];
        setCmsItems(pageData?.dynamicZoneComponentsCollection?.items || []);

        // Process overview
        if (!overviewResponse.ok) {
          setIsNotFound(true);
          return;
        }

        const overviewData = await overviewResponse.json();
        const institution = overviewData?.institutionDetails;
        const courseTitle = overviewData?.courseDetails?.courseTitle || "";
        setReviews(overviewData?.reviews || []);

        // Process enquiry response — extract userDetails for signed-in user
        if (enquiryData?.userDetails) {
          setUserDetails(enquiryData.userDetails);
        }

        if (institution) {
          setSubmissionContext({
            collegeId,
            courseId,
            subOrderItemId: maybeSubOrderSegment ? maybeSubOrderSegment.replace("n-", "") : undefined,
            institutionId: institution.institutionId != null ? String(institution.institutionId) : undefined,
            institutionName: institution.institutionName ?? undefined,
            institutionNameDisplay: institution.institutionNameDisplay ?? undefined,
            institutionTextKey: institution.institutionTextKey ?? undefined,
            institutionLogo: institution.institutionLogo ?? undefined,
            institutionHeroImage: institution.institutionHeroImage ?? undefined,
            overallRating: institution.overallRating ?? undefined,
            overallRatingExact: institution.overallRatingExact ?? undefined,
            reviewCount: institution.reviewCount ?? undefined,
            clientConsentFlag: institution.clientConsentFlag ?? undefined,
            clientConsentDate: institution.clientConsentDate ?? undefined,
            consentText: institution.consentText ?? undefined,
            collegeName: institution.institutionNameDisplay ?? undefined,
            courseName: courseTitle || undefined,
          });
        } else {
          setIsNotFound(true);
        }
      } catch (error) {
        console.error("Failed to fetch lead form data:", error);
        setIsNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (isNotFound) {
    return (
      <section className="bg-grey-50 py-[24px] min-h-screen flex items-center justify-center">
        <p className="text-grey-500">Page not found</p>
      </section>
    );
  }

  if (isLoading || !submissionContext) {
    return (
      <section className="bg-grey-50 py-[24px]">
        <div className="flex flex-col lg:flex-row gap-[24px] md:gap-[40px] items-start justify-center max-w-[1054px] mx-auto lg:px-[16px] xl:px-0">
          <div className="w-full lg:w-[416px] lg:shrink-0 md:w-[598px] md:mx-auto order-2 lg:order-1">
            <div className="skeleton skeleton-text-animated !h-[300px] w-full !rounded-[8px] !m-0"></div>
          </div>
          <div className="w-full md:w-[598px] md:mx-auto lg:flex-1 order-1 lg:order-2">
            <LeadFormFieldsSkeleton />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-grey-50 py-[24px]">
      <div className="flex flex-col lg:flex-row gap-[24px] md:gap-[40px] items-start justify-center max-w-[1054px] mx-auto lg:px-[16px] xl:px-0">
        <div className="w-full lg:w-[416px] lg:shrink-0 md:w-[598px] md:mx-auto order-2 lg:order-1">
          <UniversityCard
            logoSrc={`${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${submissionContext?.institutionLogo || ""}`}
            name={submissionContext?.institutionNameDisplay || ""}
            rating={String(submissionContext?.overallRatingExact || "")}
            reviewCount={String(submissionContext?.reviewCount || "")}
            backgroundImage={`${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${submissionContext?.institutionHeroImage || ""}`}
            maxSlidesPerView={1}
            sliderClassName="leadform-slider"
            autoplay={true}
            rev_Navigation={false}
            hideReadMore={true}
            mobileSpaceBetween={16}
            collegeId={submissionContext.collegeId}
            courseName={submissionContext?.courseName}
            reviews={reviews}
          />
        </div>

        <div className="w-full md:w-[598px] md:mx-auto lg:flex-1 order-1 lg:order-2">
          <LeadformSection
            title="Request info from"
            description={submissionContext?.courseName
              ? `${submissionContext.courseName} - ${submissionContext.institutionNameDisplay || ""}`
              : (submissionContext?.institutionNameDisplay || "")}
            pageType="requestinfo"
            cmsItems={cmsItems}
            submissionContext={submissionContext}
            submitLabel="Request info"
            userDetails={userDetails}
          />
        </div>
      </div>
      <div className="flex items-center justify-center gap-[16px] px-[16px] md:gap-[24px] mt-[24px] md:mt-[40px] w-full md:w-[598px] md:mx-auto lg:ml-auto lg:mr-[calc((100vw-1054px)/2)]">
        <Logo />
      </div>
    </section>
  );
}
